// Libs
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import styled from 'styled-components';
// Hooks
import useTheme from 'hooks/useTheme';
// Services
import ApiServiceV2 from 'services/apiService/apiServiceV2';
// Actions
import { refreshDatasource, setSelectedItems } from 'core/actions';
import { sendToast } from 'screens/Dashboard/actions';
// Selectors
import {
	selectSelectedOrderTargetFilter,
	selectSelectedOrderTargetFilterId,
	selectSelectedProject,
} from 'core/selectors';
import { selectSelectedSubModule } from 'components/Modules/selectors';
import { selectProfile } from 'screens/Login/selectors';
// Utils
import ROLES from 'core/Authorization/roles';
// Components
import { AntButton, Modal, Select, Tooltip } from 'components';

const SUCCESS_STATUSES = new Set(['moved', 'already_moved']);

export const getMoveCatalogItemThemeTokens = theme => ({
	background: theme._primaryBackgroundColor,
	surface: theme._secondaryBackgroundColor,
	elevatedSurface: theme._surfaceBackgroundColor,
	disabledSurface: theme._disabledBackgroundColor,
	textPrimary: theme._highEmphasisColor,
	textSecondary: theme._mediumEmphasisColor,
	textInactive: theme._inactiveColor,
	border: theme._borderColor,
	accent: theme._secondaryColor,
	warning: theme._warningColor,
});

const ToolbarAction = styled(IconButton)`
	&& {
		color: ${({ $tokens }) => $tokens.textPrimary};
	}

	&&:hover {
		background: ${({ $tokens }) => $tokens.elevatedSurface};
		color: ${({ $tokens }) => $tokens.accent};
	}

	&&:focus-visible {
		background: ${({ $tokens }) => $tokens.elevatedSurface};
		color: ${({ $tokens }) => $tokens.accent};
		outline: 2px solid ${({ $tokens }) => $tokens.accent};
		outline-offset: 2px;
	}
`;

const MoveModal = styled(Modal)`
	.ant-modal-content {
		background: ${({ $tokens }) => $tokens.background};
		border: 1px solid ${({ $tokens }) => $tokens.border};
		border-radius: 12px;
		box-shadow: 0 20px 48px rgba(0, 0, 0, 0.28);
		overflow: hidden;
	}

	.ant-modal-header {
		background: ${({ $tokens }) => $tokens.background};
		border-bottom: 1px solid ${({ $tokens }) => $tokens.border};
		padding: 18px 24px;
	}

	.ant-modal-title {
		color: ${({ $tokens }) => $tokens.textPrimary};
		font-size: 18px;
		font-weight: 700;
		line-height: 1.4;
	}

	.ant-modal-close {
		color: ${({ $tokens }) => $tokens.textSecondary};
	}

	.ant-modal-close:hover,
	.ant-modal-close:focus-visible {
		background: ${({ $tokens }) => $tokens.elevatedSurface};
		color: ${({ $tokens }) => $tokens.accent};
	}

	.ant-modal-close:focus-visible {
		outline: 2px solid ${({ $tokens }) => $tokens.accent};
		outline-offset: -4px;
	}

	@media (max-width: 560px) {
		.ant-modal-header {
			padding: 16px 18px;
		}
	}
`;

const ModalContent = styled.div`
	--move-background: ${({ $tokens }) => $tokens.background};
	--move-surface: ${({ $tokens }) => $tokens.surface};
	--move-surface-elevated: ${({ $tokens }) => $tokens.elevatedSurface};
	--move-surface-disabled: ${({ $tokens }) => $tokens.disabledSurface};
	--move-text: ${({ $tokens }) => $tokens.textPrimary};
	--move-text-secondary: ${({ $tokens }) => $tokens.textSecondary};
	--move-text-inactive: ${({ $tokens }) => $tokens.textInactive};
	--move-border: ${({ $tokens }) => $tokens.border};
	--move-accent: ${({ $tokens }) => $tokens.accent};
	--move-warning: ${({ $tokens }) => $tokens.warning};

	background: var(--move-background);
	box-sizing: border-box;
	color: var(--move-text);
	display: flex;
	flex-direction: column;
	gap: 20px;
	max-height: min(74vh, 700px);
	overflow-y: auto;
	padding: 24px;

	@media (max-width: 560px) {
		gap: 16px;
		padding: 20px 16px;
	}
`;

const IntroRow = styled.div`
	align-items: flex-start;
	display: flex;
	gap: 16px;
	justify-content: space-between;

	@media (max-width: 480px) {
		align-items: stretch;
		flex-direction: column;
	}
`;

const IntroText = styled.p`
	color: var(--move-text-secondary);
	font-size: 14px;
	line-height: 1.55;
	margin: 0;
	max-width: 440px;
`;

const ItemBadge = styled.div`
	background: var(--move-surface-elevated);
	border: 1px solid var(--move-border);
	border-radius: 8px;
	display: grid;
	flex: 0 0 auto;
	gap: 3px;
	min-width: 112px;
	padding: 10px 12px;

	span {
		color: var(--move-text-secondary);
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.04em;
		line-height: 1.2;
		text-transform: uppercase;
	}

	strong {
		color: var(--move-text);
		font-size: 15px;
		font-variant-numeric: tabular-nums;
		line-height: 1.35;
	}
`;

const LocationCard = styled.section`
	background: var(--move-surface);
	border: 1px solid var(--move-border);
	border-left: 4px solid var(--move-accent);
	border-radius: 8px;
	display: grid;
	gap: 5px;
	padding: 14px 16px;
`;

const Overline = styled.span`
	color: var(--move-text-secondary);
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.04em;
	line-height: 1.2;
	text-transform: uppercase;
`;

const LocationTitle = styled.strong`
	color: var(--move-text);
	font-size: 16px;
	line-height: 1.4;
`;

const LocationDetail = styled.span`
	color: var(--move-text-secondary);
	font-size: 13px;
	line-height: 1.45;
`;

const DestinationSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const SectionHeading = styled.h3`
	color: var(--move-text);
	font-size: 15px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
`;

const FieldsGrid = styled.div`
	display: grid;
	gap: 14px;
	grid-template-columns: repeat(2, minmax(0, 1fr));

	@media (max-width: 560px) {
		grid-template-columns: 1fr;
	}
`;

const Field = styled.div`
	display: grid;
	gap: 7px;
	min-width: 0;
`;

const FieldLabel = styled.label`
	color: var(--move-text-secondary);
	font-size: 12px;
	font-weight: 700;
	line-height: 1.35;
`;

const DestinationSelect = styled(Select)`
	&& {
		width: 100%;
	}

	&& .ant-select-selector {
		align-items: center;
		background: var(--move-surface) !important;
		border: 1px solid var(--move-border) !important;
		border-radius: 8px !important;
		box-shadow: none !important;
		min-height: 42px;
		padding: 4px 12px !important;
		transition:
			border-color 140ms ease,
			box-shadow 140ms ease,
			background-color 140ms ease;
	}

	&& .ant-select-selection-item {
		color: var(--move-text);
		line-height: 32px;
	}

	&& .ant-select-selection-placeholder,
	&& .ant-select-arrow {
		color: var(--move-text-inactive);
	}

	&&:not(.ant-select-disabled):hover .ant-select-selector {
		border-color: var(--move-accent) !important;
	}

	&&.ant-select-focused .ant-select-selector {
		border-color: var(--move-accent) !important;
		box-shadow: 0 0 0 3px var(--move-border) !important;
	}

	&&.ant-select-disabled .ant-select-selector {
		background: var(--move-surface-disabled) !important;
		border-color: var(--move-border) !important;
		color: var(--move-text-inactive);
		cursor: not-allowed;
		opacity: 1;
	}
`;

const TransferSummary = styled.section`
	background: var(--move-surface-elevated);
	border: 1px solid var(--move-border);
	border-radius: 8px;
	display: grid;
	gap: 12px;
	padding: 14px 16px;
`;

const RouteGrid = styled.div`
	align-items: center;
	display: grid;
	gap: 12px;
	grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);

	@media (max-width: 480px) {
		align-items: start;
		grid-template-columns: 1fr;
	}
`;

const RoutePoint = styled.div`
	display: grid;
	gap: 3px;
	min-width: 0;
`;

const RouteProject = styled.strong`
	color: var(--move-text);
	font-size: 14px;
	line-height: 1.4;
	overflow-wrap: anywhere;
`;

const RouteBusinessLine = styled.span`
	color: var(--move-text-secondary);
	font-size: 12px;
	line-height: 1.4;
	overflow-wrap: anywhere;
`;

const RouteArrow = styled.span`
	color: var(--move-accent);
	display: inline-flex;

	@media (max-width: 480px) {
		transform: rotate(90deg);
	}
`;

const SummaryHint = styled.p`
	color: var(--move-text-secondary);
	font-size: 12px;
	line-height: 1.5;
	margin: 0;
`;

const GuidanceNotice = styled.p`
	background: var(--move-surface-elevated);
	border: 1px solid var(--move-border);
	border-radius: 8px;
	color: var(--move-text-secondary);
	font-size: 13px;
	line-height: 1.5;
	margin: 0;
	padding: 12px 14px;
`;

const ErrorNotice = styled.div`
	background: var(--move-surface);
	border: 1px solid var(--move-warning);
	border-radius: 8px;
	color: var(--move-text);
	font-size: 13px;
	line-height: 1.5;
	padding: 12px 14px;
`;

const ActionFooter = styled.div`
	align-items: center;
	background: var(--move-background);
	border-top: 1px solid var(--move-border);
	display: flex;
	gap: 10px;
	justify-content: flex-end;
	margin: 0 -24px -24px;
	padding: 16px 24px 20px;

	@media (max-width: 560px) {
		margin: 0 -16px -20px;
		padding: 14px 16px 16px;

		button {
			flex: 1;
			min-width: 0;
		}
	}
`;

const ActionButton = styled(AntButton)`
	&& {
		background-color: ${({ $intent }) =>
			$intent === 'primary' ? 'var(--move-accent)' : 'var(--move-surface)'};
		border-color: ${({ $intent }) =>
			$intent === 'primary' ? 'var(--move-accent)' : 'var(--move-border)'};
		border-radius: 8px;
		box-shadow: none;
		color: ${({ $intent }) =>
			$intent === 'primary' ? 'var(--move-background)' : 'var(--move-text)'};
		font-weight: 700;
		min-height: 42px;
		min-width: 112px;
		padding: 0 18px;
		transition:
			background-color 140ms ease,
			border-color 140ms ease,
			box-shadow 140ms ease,
			color 140ms ease,
			transform 140ms cubic-bezier(0.23, 1, 0.32, 1);
	}

	&&:hover,
	&&:focus {
		background-color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--move-accent)'
				: 'var(--move-surface-elevated)'};
		border-color: var(--move-accent);
		box-shadow: 0 0 0 2px var(--move-border);
		color: ${({ $intent }) =>
			$intent === 'primary' ? 'var(--move-background)' : 'var(--move-accent)'};
	}

	&&:focus-visible {
		outline: 2px solid var(--move-accent);
		outline-offset: 2px;
	}

	&&:active {
		background-color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--move-accent)'
				: 'var(--move-surface-elevated)'};
		border-color: var(--move-accent);
		color: ${({ $intent }) =>
			$intent === 'primary' ? 'var(--move-background)' : 'var(--move-accent)'};
		transform: scale(0.98);
	}

	&&[disabled],
	&&[disabled]:hover,
	&&[disabled]:focus,
	&&[disabled]:active {
		background-color: var(--move-surface-disabled);
		border-color: var(--move-border);
		box-shadow: none;
		color: var(--move-text-inactive);
		cursor: not-allowed;
		opacity: 1;
		outline: none;
		transform: none;
	}

	&&.ant-btn-loading,
	&&.ant-btn-loading[disabled],
	&&.ant-btn-loading[disabled]:hover,
	&&.ant-btn-loading[disabled]:focus,
	&&.ant-btn-loading[disabled]:active {
		background-color: var(--move-accent);
		border-color: var(--move-accent);
		box-shadow: none;
		color: var(--move-background);
		opacity: 1;
		transform: none;
	}

	&&.ant-btn-loading .anticon {
		color: inherit;
	}

	@media (prefers-reduced-motion: reduce) {
		&& {
			transition: none;
		}

		&&:active {
			transform: none;
		}
	}
`;

export const canShowMoveCatalogItemAction = ({ isCatalog, selectedItems }) =>
	!!isCatalog && selectedItems?.length === 1;

export const getMoveCatalogItemErrorMessage = (
	error,
	fallback = 'No se pudo mover el artículo',
) => {
	if (Array.isArray(error)) {
		return error.find(item => item?.message)?.message || fallback;
	}

	return (
		error?.message ||
		error?.errors?.find(item => item?.message)?.message ||
		error?.response?.data?.message ||
		fallback
	);
};

const MoveCatalogItem = ({ itemId }) => {
	const dispatch = useDispatch();
	const { theme } = useTheme();
	const profile = useSelector(selectProfile);
	const selectedSubModule = useSelector(selectSelectedSubModule);
	const sourceProject = useSelector(selectSelectedProject);
	const sourceOrderTarget = useSelector(selectSelectedOrderTargetFilter);
	const sourceOrderTargetId = useSelector(selectSelectedOrderTargetFilterId);

	const [isOpen, setIsOpen] = useState(false);
	const [projects, setProjects] = useState([]);
	const [orderTargets, setOrderTargets] = useState([]);
	const [selectedProjectId, setSelectedProjectId] = useState();
	const [targetOrderTargetId, setTargetOrderTargetId] = useState();
	const [isLoadingProjects, setIsLoadingProjects] = useState(false);
	const [isLoadingOrderTargets, setIsLoadingOrderTargets] = useState(false);
	const [isMoving, setIsMoving] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const moduleItemId = selectedSubModule?.id;
	const canMove = !!profile?.user?.roles?.[ROLES.INVENTORY.CATALOGUE.L1];
	const selectedDestinationProject = useMemo(
		() =>
			projects.find(
				project => String(project.id) === String(selectedProjectId),
			),
		[projects, selectedProjectId],
	);
	const selectedDestinationOrderTarget = useMemo(
		() =>
			orderTargets.find(
				orderTarget => String(orderTarget.id) === String(targetOrderTargetId),
			),
		[orderTargets, targetOrderTargetId],
	);

	useEffect(() => {
		if (!isOpen || !moduleItemId) return undefined;

		let isMounted = true;
		setIsLoadingProjects(true);
		setProjects([]);
		setSelectedProjectId(undefined);
		setOrderTargets([]);
		setTargetOrderTargetId(undefined);
		setErrorMessage('');

		ApiServiceV2.get({
			url: `/departments/v1/projects/${moduleItemId}`,
		})
			.then(data => {
				if (isMounted) setProjects(Array.isArray(data) ? data : []);
			})
			.catch(error => {
				if (!isMounted) return;
				setErrorMessage(
					getMoveCatalogItemErrorMessage(
						error,
						'No se pudieron cargar los proyectos disponibles',
					),
				);
			})
			.finally(() => {
				if (isMounted) setIsLoadingProjects(false);
			});

		return () => {
			isMounted = false;
		};
	}, [isOpen, moduleItemId]);

	useEffect(() => {
		if (!isOpen || !moduleItemId || !selectedProjectId) return undefined;

		let isMounted = true;
		setIsLoadingOrderTargets(true);
		setOrderTargets([]);
		setTargetOrderTargetId(undefined);
		setErrorMessage('');

		ApiServiceV2.get({
			url: `/modules/v1/orderTargets/${moduleItemId}/${selectedProjectId}`,
		})
			.then(data => {
				if (isMounted) setOrderTargets(Array.isArray(data) ? data : []);
			})
			.catch(error => {
				if (!isMounted) return;
				setErrorMessage(
					getMoveCatalogItemErrorMessage(
						error,
						'No se pudieron cargar las líneas de negocio del proyecto',
					),
				);
			})
			.finally(() => {
				if (isMounted) setIsLoadingOrderTargets(false);
			});

		return () => {
			isMounted = false;
		};
	}, [isOpen, moduleItemId, selectedProjectId]);

	const clearModal = () => {
		setIsOpen(false);
		setProjects([]);
		setOrderTargets([]);
		setSelectedProjectId(undefined);
		setTargetOrderTargetId(undefined);
		setErrorMessage('');
	};

	const handleClose = () => {
		if (!isMoving) clearModal();
	};

	const handleMove = async () => {
		if (!targetOrderTargetId || isMoving) return;

		setIsMoving(true);
		setErrorMessage('');
		try {
			const response = await ApiServiceV2.patch({
				url: `/items/${itemId}/order-targets/${sourceOrderTargetId}`,
				data: { targetOrderTargetId },
			});

			if (!SUCCESS_STATUSES.has(response?.status)) {
				throw new Error('No se pudo confirmar el movimiento del artículo');
			}

			clearModal();
			dispatch(setSelectedItems([]));
			dispatch(refreshDatasource());
			dispatch(
				sendToast({
					message: 'Artículo movido correctamente',
					type: 'success',
					duration: 3,
				}),
			);
		} catch (error) {
			setErrorMessage(getMoveCatalogItemErrorMessage(error));
		} finally {
			setIsMoving(false);
		}
	};

	if (!canMove) return null;

	const themeTokens = getMoveCatalogItemThemeTokens(theme);
	const sourceProjectLabel = sourceProject?.name || 'Proyecto actual';
	const sourceOrderTargetLabel =
		sourceOrderTarget?.name ||
		(sourceOrderTargetId ? `ID ${sourceOrderTargetId}` : 'Actual');
	const canSubmit =
		!!itemId &&
		!!sourceOrderTargetId &&
		!!targetOrderTargetId &&
		!isLoadingProjects &&
		!isLoadingOrderTargets &&
		!isMoving;

	return (
		<>
			<Tooltip title='Mover artículo'>
				<ToolbarAction
					$tokens={themeTokens}
					aria-label='Mover artículo de catálogo'
					onClick={() => setIsOpen(true)}
				>
					<DriveFileMoveOutlinedIcon htmlColor={theme._highEmphasisColor} />
				</ToolbarAction>
			</Tooltip>

			<MoveModal
				$tokens={themeTokens}
				visible={isOpen}
				title='Mover artículo de catálogo'
				footer={null}
				width={640}
				closable={!isMoving}
				maskClosable={!isMoving}
				keyboard={!isMoving}
				onCancel={handleClose}
				destroyOnClose
			>
				<ModalContent $tokens={themeTokens}>
					<IntroRow>
						<IntroText>
							Seleccione la ubicación correcta. El artículo conservará su
							información y solo cambiará su asignación de catálogo.
						</IntroText>
						<ItemBadge aria-label={`Artículo ${itemId}`}>
							<span>Artículo</span>
							<strong>{itemId}</strong>
						</ItemBadge>
					</IntroRow>

					<LocationCard aria-labelledby='move-catalog-source-heading'>
						<Overline id='move-catalog-source-heading'>Origen actual</Overline>
						<LocationTitle>{sourceProjectLabel}</LocationTitle>
						<LocationDetail>
							Línea de negocio · {sourceOrderTargetLabel}
						</LocationDetail>
					</LocationCard>

					<DestinationSection aria-labelledby='move-catalog-destination-heading'>
						<SectionHeading id='move-catalog-destination-heading'>
							Nuevo destino
						</SectionHeading>
						<FieldsGrid>
							<Field>
								<FieldLabel htmlFor='move-catalog-project'>
									Proyecto destino
								</FieldLabel>
								<DestinationSelect
									id='move-catalog-project'
									aria-label='Proyecto destino'
									placeholder='Seleccione un proyecto'
									value={selectedProjectId}
									loading={isLoadingProjects}
									aria-busy={isLoadingProjects}
									disabled={isMoving}
									onChange={projectId => setSelectedProjectId(projectId)}
								>
									{projects.map(project => (
										<Select.Option key={project.id} value={project.id}>
											{project.name}
										</Select.Option>
									))}
								</DestinationSelect>
							</Field>

							<Field>
								<FieldLabel htmlFor='move-catalog-business-line'>
									Línea de negocio destino
								</FieldLabel>
								<DestinationSelect
									id='move-catalog-business-line'
									aria-label='Línea de negocio destino'
									placeholder='Seleccione una línea de negocio'
									value={targetOrderTargetId}
									loading={isLoadingOrderTargets}
									aria-busy={isLoadingOrderTargets}
									disabled={!selectedProjectId || isMoving}
									onChange={orderTargetId =>
										setTargetOrderTargetId(orderTargetId)
									}
								>
									{orderTargets.map(orderTarget => {
										const isSource =
											String(orderTarget.id) === String(sourceOrderTargetId);
										return (
											<Select.Option
												key={orderTarget.id}
												value={orderTarget.id}
												disabled={isSource}
											>
												{orderTarget.name}
												{isSource ? ' (origen actual)' : ''}
											</Select.Option>
										);
									})}
								</DestinationSelect>
							</Field>
						</FieldsGrid>
					</DestinationSection>

					{selectedDestinationProject && selectedDestinationOrderTarget ? (
						<TransferSummary
							role='status'
							aria-live='polite'
							aria-label='Resumen del movimiento'
						>
							<Overline>Resumen del movimiento</Overline>
							<RouteGrid>
								<RoutePoint>
									<Overline>Origen</Overline>
									<RouteProject>{sourceProjectLabel}</RouteProject>
									<RouteBusinessLine>
										{sourceOrderTargetLabel}
									</RouteBusinessLine>
								</RoutePoint>
								<RouteArrow aria-hidden='true'>
									<ArrowForwardRoundedIcon fontSize='small' />
								</RouteArrow>
								<RoutePoint>
									<Overline>Destino</Overline>
									<RouteProject>{selectedDestinationProject.name}</RouteProject>
									<RouteBusinessLine>
										{selectedDestinationOrderTarget.name}
									</RouteBusinessLine>
								</RoutePoint>
							</RouteGrid>
							<SummaryHint>
								El artículo dejará de aparecer en el origen y quedará disponible
								en el destino. No se creará una copia.
							</SummaryHint>
						</TransferSummary>
					) : (
						<GuidanceNotice>
							Seleccione el proyecto y la línea de negocio para revisar el
							cambio antes de mover el artículo.
						</GuidanceNotice>
					)}

					{!!errorMessage && (
						<ErrorNotice role='alert'>{errorMessage}</ErrorNotice>
					)}

					<ActionFooter>
						<ActionButton
							htmlType='button'
							disabled={isMoving}
							onClick={handleClose}
						>
							Cancelar
						</ActionButton>
						<ActionButton
							$intent='primary'
							type='primary'
							htmlType='button'
							loading={isMoving}
							disabled={!canSubmit}
							aria-busy={isMoving}
							onClick={handleMove}
						>
							Mover
						</ActionButton>
					</ActionFooter>
				</ModalContent>
			</MoveModal>
		</>
	);
};

export default MoveCatalogItem;
